/**
 * @fileoverview Unified system document management page for report templates,
 * email templates, and uploaded document assets.
 * @module system-web/pages/reports/SystemDocumentsPage
 *
 * Responsibilities:
 * - Render the system document grid with template/file actions.
 * - Open the new-template chooser for scratch, library, and AI creation flows.
 * - Delegate uploaded-file SAS download URL generation to the service boundary.
 *
 * Dependencies:
 * - @firesquirrel/ui-admin GridPageShell/NewDocumentEntryModal.
 * - System local-first document services backed by Dexie.
 * - openAsyncUrlWithPopupFallback for browser-safe file opening.
 *
 * Architecture:
 * - Local-first reads and mutations use system Dexie services.
 * - Blob download URLs are inherently online and resolved by systemUploadedFileService.
 *
 * Notes:
 * - System-only visibility controls differ from tenant document management,
 *   but the grid/action pattern stays aligned with tenant-web.
 *
 * @component SystemDocumentsPage
 * @localFirst
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Plus, ChevronDown, Pencil, Globe, Lock, Eye, EyeOff, Users, AlertCircle, X, Trash2, Send, Archive } from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridFilter } from '@ui-admin/components/DataGrid/types';
import type { ReportTemplateLocal, EmailTemplateLocal, UploadedFileLocal } from '@firesquirrel/local-db';
import { listReportTemplates, toggleReportTemplateLibrary, setReportTemplateVisibility, deleteReportTemplate, publishReportTemplate, archiveReportTemplate } from '../../services/systemReportTemplateService';
import { listEmailTemplates, toggleEmailTemplateLibrary, setEmailTemplateVisibility, deleteEmailTemplate, publishEmailTemplate, archiveEmailTemplate } from '../../services/systemEmailTemplateService';
import { listUploadedFiles, deleteUploadedFile, getUploadedFileDownloadUrl } from '../../services/systemUploadedFileService';
import FileUploadModal from '../../components/FileUploadModal';
import { openAsyncUrlWithPopupFallback } from '../../utils/browserNavigation';
import { NewDocumentEntryModal } from '@firesquirrel/ui-admin';
import type { DocumentCreationMethod } from '@firesquirrel/ui-admin';

type VisibilityLevel = 'all-departments' | 'specific-tenants' | 'system-only';

type UnifiedRow = {
  _id: string;
  name: string;
  description?: string;
  rowType: 'document' | 'permit' | 'email' | 'file';
  status?: string;
  scope?: { level: string };
  isInLibrary?: boolean;
  visibility?: VisibilityLevel;
  updatedAt: string;
  mimeType?: string;
  sizeBytes?: number;
  original: ReportTemplateLocal | EmailTemplateLocal | UploadedFileLocal;
};

const columns: DataGridColumn<UnifiedRow>[] = [
  {
    id: 'name',
    header: 'Name',
    accessorKey: 'name',
    cell: (info) => {
      const row = info.row.original;
      return (
        <div>
          <div className="font-medium text-gray-900">{row.name}</div>
          {row.description && (
            <div className="text-sm text-gray-500">{row.description}</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'type',
    header: 'Type',
    cell: (info) => {
      const row = info.row.original;
      if (!row.rowType) return <span className="text-gray-400">—</span>;
      const typeColors: Record<string, string> = {
        document: 'bg-blue-100 text-blue-800',
        permit: 'bg-green-100 text-green-800',
        email: 'bg-purple-100 text-purple-800',
        file: 'bg-gray-100 text-gray-800',
      };
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            typeColors[row.rowType]
          }`}
        >
          {row.rowType.charAt(0).toUpperCase() + row.rowType.slice(1)}
        </span>
      );
    },
  },
  {
    id: 'scope',
    header: 'Scope',
    cell: (info) => {
      const row = info.row.original;
      if (!row.scope) return <span className="text-gray-400">—</span>;
      const isSystem = row.scope?.level === 'system';
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            isSystem
              ? 'bg-purple-100 text-purple-800'
              : 'bg-blue-100 text-blue-800'
          }`}
        >
          {isSystem ? 'System' : 'Dept'}
        </span>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: (info) => {
      const row = info.row.original;
      if (!row.status) return <span className="text-gray-400">—</span>;
      const statusColors: Record<string, string> = {
        draft: 'bg-gray-100 text-gray-800',
        published: 'bg-green-100 text-green-800',
        archived: 'bg-yellow-100 text-yellow-800',
      };
      return (
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            statusColors[row.status] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {row.status ? row.status.charAt(0).toUpperCase() + row.status.slice(1) : 'Unknown'}
        </span>
      );
    },
  },
  {
    id: 'library',
    header: 'Library',
    cell: (info) => {
      const row = info.row.original;
      if (row.rowType === 'file') return <span className="text-gray-300">—</span>;
      return row.isInLibrary
        ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><Globe className="h-3.5 w-3.5" /> In Library</span>
        : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3.5 w-3.5" /> Not in Library</span>;
    },
  },
  {
    id: 'visibility',
    header: 'Visibility',
    cell: (info) => {
      const row = info.row.original;
      if (row.rowType === 'file' || !row.isInLibrary) return <span className="text-gray-300">—</span>;
      const v = row.visibility ?? 'all-departments';
      const visConfig: Record<string, { icon: typeof Globe; label: string; cls: string }> = {
        'all-departments': { icon: Globe, label: 'All Departments', cls: 'text-green-700' },
        'specific-tenants': { icon: Users, label: 'Specific Tenants', cls: 'text-blue-700' },
        'system-only': { icon: EyeOff, label: 'System Only', cls: 'text-gray-500' },
      };
      const cfg = visConfig[v] ?? visConfig['all-departments'];
      const Icon = cfg.icon;
      return <span className={`inline-flex items-center gap-1 text-xs ${cfg.cls}`}><Icon className="h-3.5 w-3.5" /> {cfg.label}</span>;
    },
  },
  {
    id: 'updatedAt',
    header: 'Last Updated',
    accessorKey: 'updatedAt',
    cell: (info) => new Date(info.row.original.updatedAt).toLocaleDateString(),
  },
];

const filters: DataGridFilter[] = [
  {
    id: 'rowType',
    label: 'Type',
    type: 'status',
    options: [
      { value: 'document', label: 'Document', color: '#3b82f6' },
      { value: 'permit', label: 'Permit', color: '#16a34a' },
      { value: 'email', label: 'Email', color: '#9333ea' },
      { value: 'file', label: 'File', color: '#6b7280' },
    ],
  },
  {
    id: 'status',
    label: 'Status',
    type: 'status',
    options: [
      { value: 'draft', label: 'Draft', color: '#6b7280' },
      { value: 'published', label: 'Published', color: '#16a34a' },
      { value: 'archived', label: 'Archived', color: '#d97706' },
    ],
  },
];

export default function SystemDocumentsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<UnifiedRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Department-sharing filter — mirrors the templates' `visibility` field.
  // 'all' shows everything; the other values filter rows by their visibility.
  // Uploaded files carry no visibility, so they only appear under 'all'.
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'system-only' | 'all-departments' | 'specific-tenants'>('all');
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UnifiedRow | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);

  useEffect(() => {
    loadAllItems();
  }, []);

  async function loadAllItems() {
    setLoading(true);
    try {
      const [documents, emails, files] = await Promise.all([
        listReportTemplates(),
        listEmailTemplates(),
        listUploadedFiles(),
      ]);

      const unified: UnifiedRow[] = [
        ...documents.map((doc: ReportTemplateLocal) => ({
          _id: doc._id,
          name: doc.name,
          description: doc.description,
          rowType: doc.documentType === 'permit' ? 'permit' : 'document',
          status: doc.status,
          scope: doc.scope,
          isInLibrary: doc.isInLibrary,
          visibility: doc.visibility,
          updatedAt: doc.updatedAt,
          original: doc,
        })) as UnifiedRow[],
        ...emails.map((email: EmailTemplateLocal) => ({
          _id: email._id,
          name: email.name,
          description: email.description,
          rowType: 'email',
          status: email.status,
          isInLibrary: (email as any).isInLibrary,
          visibility: (email as any).visibility,
          scope: (email as any).scope,
          updatedAt: email.updatedAt,
          original: email,
        })) as UnifiedRow[],
        ...files.map((file: UploadedFileLocal) => ({
          _id: file._id,
          name: file.name,
          description: file.description,
          rowType: 'file',
          updatedAt: file.updatedAt,
          mimeType: file.mimeType,
          sizeBytes: file.sizeBytes,
          original: file,
        })) as UnifiedRow[],
      ];

      setItems(unified);
    } catch (error) {
      console.error('[SystemDocumentsPage] Failed to load items:', error);
      setErrorMessage('Failed to load templates. Please refresh the page or contact support if the issue persists.');
    } finally {
      setLoading(false);
    }
  }

  // Client-side visibility filter — rows without a visibility (uploaded files)
  // only appear unfiltered.
  const visibleItems = useMemo(
    () => (visibilityFilter === 'all' ? items : items.filter((row) => row.visibility === visibilityFilter)),
    [items, visibilityFilter],
  );

  const handleRefresh = async () => {
    await loadAllItems();
  };

  const handleToggleLibrary = useCallback(async (row: UnifiedRow, inLibrary: boolean) => {
    try {
      setErrorMessage(null);
      if (row.rowType === 'email') {
        await toggleEmailTemplateLibrary(row._id, inLibrary);
      } else {
        await toggleReportTemplateLibrary(row._id, inLibrary);
      }
      await loadAllItems();
    } catch (err) {
      console.error('[SystemDocumentsPage] Failed to toggle library:', err);
      setErrorMessage(`Failed to ${inLibrary ? 'add to' : 'remove from'} library. Please try again.`);
    }
  }, []);

  const handleSetVisibility = useCallback(async (row: UnifiedRow, visibility: VisibilityLevel) => {
    try {
      setErrorMessage(null);
      if (row.rowType === 'email') {
        await setEmailTemplateVisibility(row._id, visibility);
      } else {
        await setReportTemplateVisibility(row._id, visibility);
      }
      await loadAllItems();
    } catch (err) {
      console.error('[SystemDocumentsPage] Failed to set visibility:', err);
      setErrorMessage('Failed to update visibility. Please try again.');
    }
  }, []);

  const handleDelete = useCallback(async (row: UnifiedRow) => {
    setDeleteTarget(row);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    
    try {
      setErrorMessage(null);
      let success = false;
      
      if (deleteTarget.rowType === 'email') {
        success = await deleteEmailTemplate(deleteTarget._id);
      } else if (deleteTarget.rowType === 'file') {
        success = await deleteUploadedFile(deleteTarget._id);
      } else {
        success = await deleteReportTemplate(deleteTarget._id);
      }
      
      if (success) {
        await loadAllItems();
      } else {
        setErrorMessage(`Failed to delete ${deleteTarget.rowType}. Please try again.`);
      }
    } catch (err) {
      console.error('[SystemDocumentsPage] Failed to delete item:', err);
      setErrorMessage(`Failed to delete ${deleteTarget.rowType}. ${err instanceof Error ? err.message : 'Please try again.'}`);
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget]);

  if (loading) return null;

  const handleNewModalSelect = (method: DocumentCreationMethod) => {
    if (method === 'scratch') {
      navigate('/document-templates/new?method=scratch');
    } else if (method === 'library') {
      navigate('/document-templates/new?method=gallery');
    } else if (method === 'ai') {
      navigate('/document-templates/new?method=ai');
    }
  };

  return (
    <>
    {errorMessage && (
      <div className="mx-4 mt-2 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="flex-1">{errorMessage}</span>
        <button onClick={() => setErrorMessage(null)} className="p-0.5 hover:bg-red-100 rounded">
          <X className="h-4 w-4" />
        </button>
      </div>
    )}
    <GridPageShell
      gridId="system-documents"
      gridType="documents"
      title="Document Templates"
      subtitle={`${visibleItems.length} ${visibleItems.length === 1 ? 'item' : 'items'}`}
      columns={columns}
      data={visibleItems}
      getRowId={(row) => row._id}
      searchPlaceholder="Search templates and files..."
      filters={filters}
      toolbarActions={
        <div className="flex items-center gap-3">
          <select
            title="Filter by department visibility"
            value={visibilityFilter}
            onChange={(e) => setVisibilityFilter(e.target.value as typeof visibilityFilter)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Items</option>
            <option value="system-only">System Only</option>
            <option value="all-departments">All Departments</option>
            <option value="specific-tenants">Specific Tenants</option>
          </select>
          <div className="relative">
            <button
              onClick={() => setShowNewMenu(!showNewMenu)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
            >
              <Plus className="h-4 w-4" />
              New
              <ChevronDown className="h-4 w-4" />
            </button>
            {showNewMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100]">
                <button
                  onClick={() => {
                    setShowNewMenu(false);
                    setShowNewModal(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  New Document Template…
                </button>
                <button
                  onClick={() => {
                    setShowNewMenu(false);
                    navigate('/email-templates/new');
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  New Email Template
                </button>
                <button
                  onClick={() => {
                    setShowNewMenu(false);
                    setShowUploadModal(true);
                  }}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Upload File
                </button>
              </div>
            )}
          </div>
        </div>
      }
      onRefresh={handleRefresh}
      emptyIcon={FileText}
      emptyTitle="No templates or files yet"
      emptySubtitle="Create your first system-level template or upload a file"
      emptyCTA={{ label: 'New Template', onClick: () => setShowNewMenu(true) }}
      onRowClick={async (row) => {
        if (row.rowType === 'file') {
          const file = row.original as UploadedFileLocal;
          // Always fetch download URL from API to get fresh SAS token
          try {
            await openAsyncUrlWithPopupFallback(
              async () => {
                return getUploadedFileDownloadUrl(file._id);
              },
              {
                blockedMessage: 'Safari or your browser blocked opening this file in a new tab. Select OK to continue in this tab, or Cancel to stay on the documents page.',
              },
            );
          } catch (err) {
            console.error('[SystemDocumentsPage] Failed to get download URL:', err);
            setErrorMessage('Failed to open file. The download URL could not be generated.');
          }
        } else if (row.rowType === 'email') {
          navigate(`/email-templates/${row._id}`);
        } else {
          navigate(`/document-templates/${row._id}`);
        }
      }}
      rowActions={[
        {
          id: 'edit',
          label: 'Edit',
          icon: Pencil,
          onClick: (row) => {
            if (row.rowType === 'email') {
              navigate(`/email-templates/${row._id}`);
            } else {
              navigate(`/document-templates/${row._id}`);
            }
          },
        },
        {
          id: 'publish',
          label: 'Publish',
          icon: Send,
          onClick: async (row) => {
            try {
              setErrorMessage(null);
              if (row.rowType === 'email') {
                await publishEmailTemplate(row._id);
              } else if (row.rowType === 'document' || row.rowType === 'permit') {
                await publishReportTemplate(row._id);
              }
              await loadAllItems();
            } catch (err) {
              console.error('[SystemDocumentsPage] Failed to publish:', err);
              setErrorMessage(`Failed to publish ${row.rowType}. ${err instanceof Error ? err.message : 'Please try again.'}`);
            }
          },
          hidden: (row) => row.rowType === 'file' || row.status === 'published' || row.status === 'archived',
        },
        {
          id: 'archive',
          label: 'Archive',
          icon: Archive,
          onClick: async (row) => {
            try {
              setErrorMessage(null);
              if (row.rowType === 'email') {
                await archiveEmailTemplate(row._id);
              } else if (row.rowType === 'document' || row.rowType === 'permit') {
                await archiveReportTemplate(row._id);
              }
              await loadAllItems();
            } catch (err) {
              console.error('[SystemDocumentsPage] Failed to archive:', err);
              setErrorMessage(`Failed to archive ${row.rowType}. ${err instanceof Error ? err.message : 'Please try again.'}`);
            }
          },
          hidden: (row) => row.rowType === 'file' || row.status === 'archived',
        },
        {
          id: 'addToLibrary',
          label: 'Add to Library',
          icon: Globe,
          onClick: async (row) => handleToggleLibrary(row, true),
          hidden: (row) => row.rowType === 'file' || !!row.isInLibrary || row.status !== 'published',
        },
        {
          id: 'removeFromLibrary',
          label: 'Remove from Library',
          icon: Lock,
          onClick: async (row) => handleToggleLibrary(row, false),
          hidden: (row) => row.rowType === 'file' || !row.isInLibrary,
        },
        {
          id: 'visibilityAll',
          label: 'Visible to All Departments',
          icon: Globe,
          onClick: async (row) => handleSetVisibility(row, 'all-departments'),
          hidden: (row) => row.rowType === 'file' || !row.isInLibrary || (row.visibility ?? 'all-departments') === 'all-departments',
        },
        {
          id: 'visibilitySpecific',
          label: 'Visible to Specific Tenants',
          icon: Users,
          onClick: async (row) => handleSetVisibility(row, 'specific-tenants'),
          hidden: (row) => row.rowType === 'file' || !row.isInLibrary || row.visibility === 'specific-tenants',
        },
        {
          id: 'visibilitySystemOnly',
          label: 'System Only (Hidden from Tenants)',
          icon: EyeOff,
          onClick: async (row) => handleSetVisibility(row, 'system-only'),
          hidden: (row) => row.rowType === 'file' || !row.isInLibrary || row.visibility === 'system-only',
        },
        {
          id: 'delete',
          label: 'Delete',
          icon: Trash2,
          onClick: async (row) => handleDelete(row),
          // Always show delete for all item types
        },
      ]}
    />

    {/* File Upload Modal */}
    <FileUploadModal
      isOpen={showUploadModal}
      onClose={() => setShowUploadModal(false)}
      onSuccess={() => {
        loadAllItems();
      }}
    />

    {/* Delete Confirmation Modal */}
    {deleteTarget && (
      <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={() => setDeleteTarget(null)}>
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative bg-white rounded-xl shadow-2xl border border-gray-200 w-[460px] p-6"
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Delete {deleteTarget.rowType}?</h3>
              <p className="text-[10px] text-gray-500">{deleteTarget.name}</p>
            </div>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed mb-4">
            This will permanently delete the <strong>{deleteTarget.rowType}</strong> "<strong>{deleteTarget.name}</strong>".
            {deleteTarget.rowType !== 'file' && ' This action cannot be undone and will remove it from all departments.'}
          </p>
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-5 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>Are you sure you want to continue?</span>
          </p>
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">
              Delete {deleteTarget.rowType}
            </button>
          </div>
        </div>
      </div>
    )}
    <NewDocumentEntryModal
      open={showNewModal}
      onClose={() => setShowNewModal(false)}
      onSelect={handleNewModalSelect}
      libraryLabel="From Gallery"
      libraryDescription="Start from a curated gallery of pre-built templates for permits, reports, certificates, and more."
    />
  </>
  );
}
